import { useState } from "react";
import {
  useRematchSalesEmails,
  useApplyRematchSalesEmails,
  type RematchSalesDiff,
  type RematchSalesDiffRow,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, GitCompareArrows } from "lucide-react";
import { toast } from "sonner";

function changeLabel(row: RematchSalesDiffRow): {
  text: string;
  variant: "default" | "secondary" | "destructive" | "outline";
} {
  switch (row.changeType) {
    case "gain":
      return { text: "Will mark Sold", variant: "default" };
    case "lose":
      return { text: "Will clear Sold", variant: "destructive" };
    case "quantity":
      return {
        text: `Qty ${row.currentSoldTicketCount} → ${row.proposedSoldTicketCount}`,
        variant: "secondary",
      };
  }
}

/**
 * Bulk "Rematch" entry point: a toolbar button that runs the dry-run rematch,
 * shows every inventory row whose Sold status would change, and lets the
 * operator Apply the selected rows or Skip all. Self-contained so emails.tsx
 * only has to render it and pass an invalidation callback.
 */
export function RematchDialog({
  disabled,
  onApplied,
}: {
  disabled?: boolean;
  onApplied: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [diff, setDiff] = useState<RematchSalesDiff | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const rematch = useRematchSalesEmails({
    mutation: {
      onSuccess: (d) => {
        if (d.rows.length === 0) {
          // Nothing to review — don't open an empty modal.
          toast.success(
            `No discrepancies found (re-ran ${d.totalProcessed} stored sale email${d.totalProcessed === 1 ? "" : "s"}).`,
          );
          return;
        }
        setDiff(d);
        // Pre-select every discrepancy so "Apply selected" is the happy path.
        setSelected(new Set(d.rows.map((r) => r.inventoryId)));
        setOpen(true);
      },
      onError: () => toast.error("Rematch failed"),
    },
  });

  const apply = useApplyRematchSalesEmails({
    mutation: {
      onSuccess: (r) => {
        toast.success(
          `Rematch applied: ${r.applied} listing${r.applied === 1 ? "" : "s"} updated (${r.emailsUpdated} email${r.emailsUpdated === 1 ? "" : "s"} re-linked)`,
        );
        setOpen(false);
        setDiff(null);
        onApplied();
      },
      onError: () => toast.error("Apply failed"),
    },
  });

  const rows = diff?.rows ?? [];
  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.inventoryId)));

  const applySelected = () => {
    const ids = new Set(selected);
    apply.mutate({
      data: {
        decisions: rows.map((r) => ({
          inventoryId: r.inventoryId,
          action: ids.has(r.inventoryId) ? "apply" : "skip",
        })),
      },
    });
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="h-8 whitespace-nowrap"
        onClick={() => rematch.mutate()}
        disabled={disabled || rematch.isPending}
        title="Re-run the sale matcher across stored emails and review any Sold-status changes before applying them. No new fetch from BigQuery."
      >
        {rematch.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <GitCompareArrows className="h-3.5 w-3.5" />
        )}
        <span className="ml-1">Rematch</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Rematch review</DialogTitle>
            <DialogDescription>
              {rows.length === 0
                ? `Re-ran the matcher across ${diff?.totalProcessed ?? 0} stored sale emails — no Sold-status changes found.`
                : `${rows.length} listing${rows.length === 1 ? "" : "s"} would change Sold status (from ${diff?.totalProcessed ?? 0} stored sale emails). Review and apply the ones you want.`}
            </DialogDescription>
          </DialogHeader>

          {rows.length > 0 && (
            <>
              <div className="flex items-center gap-2 border-b pb-2 text-xs text-muted-foreground">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleAll}
                  aria-label="Select all"
                />
                <span>Select all ({selected.size} selected)</span>
              </div>
              <ScrollArea className="max-h-[55vh] pr-3">
                <div className="flex flex-col gap-2">
                  {rows.map((row) => {
                    const lbl = changeLabel(row);
                    return (
                      <div
                        key={row.inventoryId}
                        className="flex items-start gap-3 rounded-md border p-3 text-sm"
                      >
                        <Checkbox
                          className="mt-0.5"
                          checked={selected.has(row.inventoryId)}
                          onCheckedChange={() => toggle(row.inventoryId)}
                          aria-label={`Select listing ${row.inventoryId}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">
                              {row.eventName ?? "Unknown event"}
                            </span>
                            <Badge variant={lbl.variant} className="text-[10px]">
                              {lbl.text}
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {[
                              row.reachListingId
                                ? `Listing ${row.reachListingId}`
                                : null,
                              row.section ? `Sec ${row.section}` : null,
                              row.row ? `Row ${row.row}` : null,
                              row.vendorAccountEmail,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Current:{" "}
                            {row.currentSold
                              ? `Sold (${row.currentSoldTicketCount})`
                              : "Not sold"}{" "}
                            → Proposed:{" "}
                            {row.proposedSold
                              ? `Sold (${row.proposedSoldTicketCount})`
                              : "Not sold"}
                          </div>
                          {row.emails.length > 0 && (
                            <div className="text-[11px] text-muted-foreground mt-1 flex flex-col gap-0.5">
                              <span className="font-medium">
                                {row.emails.length} email
                                {row.emails.length === 1 ? "" : "s"}:
                              </span>
                              {row.emails.map((e) => (
                                <span
                                  key={e.saleEmailId}
                                  className="truncate"
                                  title={e.emailSubject ?? `Email #${e.saleEmailId}`}
                                >
                                  {e.emailSubject?.trim()
                                    ? e.emailSubject
                                    : `Email #${e.saleEmailId}`}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={apply.isPending}
            >
              {rows.length === 0 ? "Close" : "Skip all"}
            </Button>
            {rows.length > 0 && (
              <Button
                size="sm"
                onClick={applySelected}
                disabled={apply.isPending || selected.size === 0}
              >
                {apply.isPending && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                )}
                Apply selected ({selected.size})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
